<template>
  <div class="fts-date-time" :class="[{'comfort-design': comfortDesign}, {'date-time--fw': fullWidth}]">
    <cleave
      v-model="textValue"
      :options="config.cleaveOptions"
      :placeholder="config.textInputPlaceholder"
      :error="error"
      class="date-time-text-input"
      ref="text-input"
      @blur="onBlurTextInput"
      @change="onChangeTextInput"
      v-if="isTextMode"
    />
    <input
      type="text"
      class="fts-form-control date-time-display"
      :class="{'form-control--error': error}"
      :value="displayValue"
      @focus="focusTextInput"    
      @click.self="focusTextInput"
      v-else
    />
    <font-awesome-icon
      :icon="['far', 'calendar']"
      class="date-time-popup-toggle"
      data-datetime-popup-toggle
      @click="openDatePicker"
    />
    <datetime
      :type="type"
      :use12-hour="true"
      :minute-step="1"
      :format="format"
      :min-datetime="minDatetime"
      :max-datetime="maxDatetime"
      value-zone="local"
      :flow="getFlow"
      :input-id="`ftsDateTime${uid}`"
      :class="{ 'comfort-design': comfortDesign }"
      v-model="model"
      tabindex="-1"
      @close="$emit('close')"
      ref="ftsDateTime"
      class="data-time-popup-input"
    />
  </div>
</template>
<script>
import Vue from "vue";
import Datetime from "vue-datetime";
Vue.use(Datetime);
import "vue-datetime/dist/vue-datetime.css";
import Cleave from "../cleave.js/Cleave.vue";

import commons from "../commons/commons";

export default {
  components: {
    "cleave": Cleave
  },
  props: {
    appendToBody: Boolean,
    referenceFor: String,
		minDatetime: [Date, String],
		maxDatetime: [Date, String],
		predefinedFormatId: String,
    fullWidth: Boolean,
    flow: Array,
    comfortDesign: Boolean,
    error: Boolean,
    value: [Date, String],
    formData: Object,
    item: Object
  },
  model: {
    prop: "value",
    event: "updateValue",
  },
  data: () => ({
    uid: null,
    textValue: null,
    isTextMode: false
  }),
  methods: {
    openDatePicker() {
      this.isTextMode = false;
      this.$refs.ftsDateTime.isOpen = true;
    },
    focusTextInput() {
      if (!this.model) {
        this.textValue = null;
      } else {
        const dt = luxon.fromISO(this.model);
        const formatMap = {
          time: "HH:mm",
          datetime: "dd/MM/yyyy HH:mm",
          date: "dd/MM/yyyy"
        };

        this.textValue = dt.toFormat(formatMap[this.itemType] || formatMap.date);
      }
      this.isTextMode = true;
      this.$nextTick(() => {
        this.$refs['text-input'].$el.focus();
      });
    },
    onBlurTextInput() {
      this.isTextMode = false;

      if (this.textValue) {
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const formatMap = {
          time: "HH:mm",
          datetime: "dd/MM/yyyy HH:mm",
          date: "dd/MM/yyyy",
        };

        let result = null;

        if (this.itemType === "time") {
          const time = luxon.fromFormat(this.textValue, formatMap.time);

          if (time.isValid) {
            const parsedModel = this.model ? luxon.fromISO(this.model) : null;
            const baseDate = parsedModel && parsedModel.isValid ? parsedModel : luxon.now().startOf("day");

            result = baseDate.set({
              hour: time.hour,
              minute: time.minute,
              second: 0,
              millisecond: 0,
            }).toISO();
          }
        } else {
          const dt = luxon.fromFormat(
            this.textValue,
            this.itemType === "datetime" ? formatMap.datetime : formatMap.date,
            { zone: timeZone }
          );
          const dtISO = dt.toISO();
          
          if (luxon.fromISO(dtISO, { setZone: timeZone }).isValid) {
            result = dtISO;
          }
        }

        this.model = result;
      }
    },
    onChangeTextInput(val) {
      if (!val) {
        this.model = null;
      }
    }
  },
  computed: {
    model: {
      get() {
        if (this.item && this.item.fieldName) {
          return this.formData[this.item.fieldName] || "";
        } else {
          return this.value || "";
        }
      },
      set(val) {
        if (this.itemType == "date" && val) {
          const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const dt = luxon.fromISO(val, { setZone: timeZone });

          if (
            dt.isValid && 
            (dt.hour !== 0 || dt.minute !== 0 || dt.second !== 0 || dt.millisecond !== 0)
          ) {
            val = dt.startOf("day").toISO();
          }
        }

        if (this.item && this.item.fieldName) {
          this.$set(this.formData, this.item.fieldName, val);
        } else {
          this.$emit("updateValue", val);
        }
      },
    },
    itemInput() {
      if (
        this.item &&
        (!commons.isInteger(this.item.type) || this.item.custom)
      ) {
        return this.item.input;
      }
    },
    itemType() {
      if (
        (this.item && this.item.type === 6 && this.item.predefinedFormatId == "t") || 
        this.itemInput == "time"
      ) {
        return "time";
      } else if (this.item && this.item.type == 6 || this.itemInput == "dateTime") {
        return "datetime";
      }

      return "date";
    },
    type() {
      if (this.itemType == "time") {
        return "time";
      }

      return "datetime";
    },
    format() {
      if (this.item && this.item.enableCustomDateFormat && this.item.customDateFormatId) {
        return commons.dateFormatTranslate(this.item.customDateFormatId);
			} else if (this.predefinedFormatId) {
				return commons.dateFormatTranslate(this.predefinedFormatId);
      } else if (this.item && this.item.predefinedFormatId) {
        return commons.dateFormatTranslate(this.item.predefinedFormatId);
      } else if (this.itemType == "date") {
        return commons.dateFormatTranslate("DD");
      } else if (this.itemType == "datetime") {
        return commons.dateFormatTranslate("DD t");
      } else if (this.itemType == "time") {
        return commons.dateFormatTranslate("t");
      }
    },
    config() {
      const dateOptions = {
        date: true,
        datePattern: ['d', 'm', 'Y'],
        delimiters: ['/', '/']
      }
      const timeOptions = {
        time: true,
        timePattern: ['h', 'm'],
        delimiters: [':', ':']
      }
      if (this.itemType === "time") {
        return {
          cleaveOptions: timeOptions,
          textInputPlaceholder: "HH:mm"
        }
      } else if (this.itemType === "datetime") {
        return {
          cleaveOptions: { ...dateOptions, ...timeOptions, delimiters: ['/', '/', ' ', ':', ':'] },
          textInputPlaceholder: "DD/MM/YYYY HH:mm"
        }
      } else {
        return {
          cleaveOptions: dateOptions,
          textInputPlaceholder: "DD/MM/YYYY"
        }
      }
    },
    getFlow() {
      if (this.flow) {
        return this.flow;
      } else if (this.itemType == "date") {
        return ["date"];
      }
    },
    displayValue() {
      if (this.model && this.format) {
        return luxon.fromISO(this.model).toFormat(this.format);
      }
    }
  },
  watch: {
    model(val, oldVal) {
      if ((val && !oldVal) || (!val && oldVal && val != oldVal)) {
        this.$emit("input", val);
			}
    },
  },
  async mounted() {
    this.uid = this._uid; 
    await this.$nextTick();
    
    if (this.appendToBody) {
      try {
        let appendContent = this.$refs.ftsDateTime.$children[0].$el;
        this.referenceFor && appendContent.classList.add(this.referenceFor);
        document.body.appendChild(appendContent);
      } catch (err) {
        console.warn("[DOMException]", err);
      }
    }
  },
  beforeDestroy() {
    if (this.appendToBody)
      try {
        document.body.removeChild(this.$refs.ftsDateTime.$children[0].$el);
      } catch (err) {
        console.warn("[DOMException]", err);
      }
  }
};
</script>

<style lang="scss">
$hoverColor: var(--fts-gray-100);

.fts-date-time {
  position: relative;
  max-width: 100%;
  width: 325px;
  font-size: var(--inistate-label-text-14);
  line-height: var(--inistate-paragraph-text-14--line-height);
  .date-time-display {
    cursor: pointer;
  }
  .date-time-text-input,
  .date-time-display {
    padding-right: 2.375rem;
  }
  .date-time-popup-toggle {
    position: absolute;
    top: 50%;
    right: 0.75rem;
    transform: translate(0, -50%);
    -webkit-transform: translate(0, -50%);
    -moz-transform: translate(0, -50%);
    -ms-transform: translate(0, -50%);
    color: var(--fts-default);
    cursor: pointer;
    &:hover {
      color: var(--fts-primary);
    }
  }
  .data-time-popup-input {
    height: 0;
    width: 0;
    overflow: hidden;
  }
  &.comfort-design {
    width: 375px;
  }
  &.date-time--fw {
    width: 100%;
  }
}
/* popup */
.vdatetime-popup {
  background-color: var(--fts-card-background);
  color: var(--fts-text);
  overflow: auto;
  white-space: normal;
  .vdatetime-popup__header {
    background-color: var(--fts-primary) !important;
  }
}
.vdatetime-year-picker__item--selected {
  color: var(--fts-primary) !important;
}
.vdatetime-year-picker__item:hover {
  background-color: $hoverColor;
}
.vdatetime-month-picker__item--selected {
  color: var(--fts-primary) !important;
}
.vdatetime-month-picker__item:hover {
  background-color: $hoverColor;
}
.vdatetime-calendar__month__day:hover > span > span {
  background-color: $hoverColor;
}
.vdatetime-calendar__month__day--selected > span > span,
.vdatetime-calendar__month__day--selected:hover > span > span {
  background-color: var(--fts-primary) !important;
}
.vdatetime-time-picker__item--selected {
  color: var(--fts-primary) !important;
}
.vdatetime-time-picker__item:hover {
  background-color: $hoverColor;
}
.vdatetime-popup__actions__button {
  color: var(--fts-primary) !important;
}
/* media query */
@media screen and (min-height: 550px) {
  .vdatetime-popup {
    top: 55px;
    transform: translateX(-50%);
  }
}
@media screen and (max-height: 549.98px) {
  .vdatetime-popup {
    max-height: 100%;
  }
}
</style>